import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GzFormFieldToggleableWebComponent, GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../ui/gzInput/component/GzInputWebComponent';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzTitleWebComponent } from '../../../ui/gzTitle';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { LicenseConstants } from '../../LicenseConstants';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { RegexValidator } from '../../../ui/gzForm/validators/regex.validator';
import { CompanyType } from '../../../company/types/company-type';
import { FormValidator } from '../../../ui/gzForm/formField/types/form-validator';
import { LicenseStatus } from '../../types/LicenseStatus';
import { LicenseExceptions } from '../../types/LicenseExceptions';
import { Subscription } from 'rxjs';
import { LicensePrePaidFormViewData } from '../types/LicensePrePaidFormViewData';
import { GzHardCodedFormField } from '../../../ui/gzForm/formField/component/GzHardCodedFormField';
import {
  WEB_COMPONENT_GZ_FORM_FIELD,
  WEB_COMPONENT_GZ_FORM_FIELD_TOGGLEABLE,
  WEB_COMPONENT_MDR_CONTACT_DETAILS_SECTION,
} from '../../../webcomponents.constants';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { ActionEnum } from '../../../company/common/addProductMenu/types/ActionEnum';
import { getLocalizedAsset } from '../../../utils/i18n.utils';
import { GzInfoBoxWebComponent } from '../../../ui/gzInfoBox';
import { htmlEncode } from '../../../utils/escape.utils';
import { LicenseProductIdEnum } from '@nirvana/shared/license-helper';
import { LicenseActivationDenyCodes } from '../../types/LicenseActivationDenyCodes';
import { ContactDetailsSectionWebComponent } from '../../../company/common/mdrContactDetails/mdrContactDetailsSection';
import { MdrContactInformationDbSchema } from '../../../company/types/company-data';
import { MdrContactImportSourceValue } from '../../../company/common/mdrContactDetails/common/types';

export class LicensePrePaidSubFormView extends BaseWebComponentView {
  public dom: {
    $wrapper: HTMLElement;
    $licensePrePaidFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $checkLicenseValidityButton: GzButtonWebComponent;
    $infoMessageForDisabledLicenseKeyField: HTMLElement;
    $formFields: GzFormFieldWebComponent[];
    $formFieldsToggleable: GzFormFieldToggleableWebComponent[];

    $incompatibleProductNotice: GzInfoBoxWebComponent;
    $incompatibleAddonWithProductError: GzInfoBoxWebComponent;
    $validLicenseWithIssuesWarning: GzInfoBoxWebComponent;

    licenseDetails: {
      $licenseDetailsContainer: HTMLElement;
      $productDetailsTitle: GzTitleWebComponent;
      $productName: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $validity: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $availableSeats: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $availableSeatsForServers: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $availableSeatsForExchangeMailboxes: GzFormFieldWebComponent<GzInputWebComponent, string>;
      $availableSeatsForMobileDevices: GzFormFieldWebComponent<GzInputWebComponent, string>;
    };

    $mdrContactDetailsSection: ContactDetailsSectionWebComponent;
  };

  public licenseStatusMessages: {};
  public licenseExceptionMessages: {};

  public formGroup!: FormGroup;

  public isAddonProductFormField: GzHardCodedFormField<boolean | undefined> = new GzHardCodedFormField<
    boolean | undefined
  >(undefined);
  public productIdFormField: GzHardCodedFormField<LicenseProductIdEnum | undefined> = new GzHardCodedFormField<
    LicenseProductIdEnum | undefined
  >(undefined);

  private customPrePaidFieldValidators: Array<FormValidator>;

  private subscriptions: Subscription[] = [];

  private companyType?: CompanyType;
  public returnIncompatibleProducts: boolean;
  public viewData?: LicensePrePaidFormViewData;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#license-pre-paid-form-wrapper'),
      $licensePrePaidFormField: this.shadow.querySelector('#license-pre-paid-form-field'),
      $checkLicenseValidityButton: this.shadow.querySelector('#check-license-validity-button'),
      $infoMessageForDisabledLicenseKeyField: this.shadow.querySelector('#info-message-for-disabled-license-key-field'),
      $formFields: this.shadow.querySelectorAll(WEB_COMPONENT_GZ_FORM_FIELD),
      $formFieldsToggleable: this.shadow.querySelectorAll(WEB_COMPONENT_GZ_FORM_FIELD_TOGGLEABLE),

      $incompatibleProductNotice: this.shadow.querySelector('#incompatible-product-notice-container'),
      $incompatibleAddonWithProductError: this.shadow.querySelector('#incompatible-addon-with-product-error-container'),
      $validLicenseWithIssuesWarning: this.shadow.querySelector('#valid-license-with-issues-warning-container'),

      licenseDetails: {
        $licenseDetailsContainer: this.shadow.querySelector('#pre-paid-license-details'),
        $productDetailsTitle: this.shadow.querySelector('#product-details-title'),
        $productName: this.shadow.querySelector('#product-name'),
        $validity: this.shadow.querySelector('#validity'),
        $availableSeats: this.shadow.querySelector('#available-seats'),
        $availableSeatsForServers: this.shadow.querySelector('#available-seats-for-servers'),
        $availableSeatsForExchangeMailboxes: this.shadow.querySelector('#available-seats-for-exchange-mailboxes'),
        $availableSeatsForMobileDevices: this.shadow.querySelector('#available-seats-for-mobile-devices'),
      },

      $mdrContactDetailsSection: this.shadow.querySelector(WEB_COMPONENT_MDR_CONTACT_DETAILS_SECTION),
    };

    this.setTranslatedStrings();
    this.buildIncompatibleProductNoticeInfoBox();
    this.buildIncompatibleAddonWithProductError();
  }

  setTranslatedStrings() {
    this.dom.$licensePrePaidFormField.label = this.dependencies.translateFn('License key');
    this.dom.$licensePrePaidFormField.Input.placeholder = this.dependencies.translateFn('Enter the license key');

    this.dom.$checkLicenseValidityButton.innerText = this.dependencies.translateFn('Check validity');

    this.dom.licenseDetails.$productDetailsTitle.innerText = this.dependencies.translateFn('Product Details');
    this.dom.licenseDetails.$productName.label = this.dependencies.translateFn('Product name');
    this.dom.licenseDetails.$validity.label = this.dependencies.translateFn('Valid until');
    this.dom.licenseDetails.$availableSeats.label = this.dependencies.translateFn('License seats');
    this.dom.licenseDetails.$availableSeatsForServers.label = this.dependencies.translateFn('Available for servers');
    this.dom.licenseDetails.$availableSeatsForExchangeMailboxes.label =
      this.dependencies.translateFn('Exchange mailboxes');
    this.dom.licenseDetails.$availableSeatsForMobileDevices.label = this.dependencies.translateFn('Mobile devices');
  }

  public resetLicensePrePaidForm() {
    this.resetLicensePrePaidFormField();
    this.resetLicenseDetailsContainer();

    if (this.dom.$mdrContactDetailsSection.started) {
      this.stopMdrContactDetailsSection();
    }
  }

  public updateViewData(viewData: LicensePrePaidFormViewData) {
    this.viewData = viewData;
    this.setReturnIncompatibleProducts();
  }

  public setViewData(viewData: LicensePrePaidFormViewData) {
    // view data already set, skip
    if (this.viewData) {
      return;
    }

    this.viewData = viewData;

    this.setLicenseMessages();
    this.setReturnIncompatibleProducts();
    this.createFormGroup();
    this.setCustomPrePaidFieldValidators();
    this.setupLicenseInput();
  }

  private setMdrContactDetailsViewData(mdrContactInformation?: MdrContactInformationDbSchema): void {
    const insideAddProductMenu = this.viewData?.action !== undefined;

    let companyCanBeManagedByCurrentUser: boolean;

    if (this.viewData?.companyVisibilityTree) {
      companyCanBeManagedByCurrentUser =
        this.dependencies.getUser().visibilityTree === this.viewData?.companyVisibilityTree;
    } else {
      companyCanBeManagedByCurrentUser = this.viewData?.canBeManagedByAbove ?? true;
    }

    this.dom.$mdrContactDetailsSection.viewData = {
      fullName: mdrContactInformation?.fullName ?? '',
      email: mdrContactInformation?.email ?? '',
      phoneNumber: mdrContactInformation?.phoneNumber ?? {
        countryCode: '',
        subscriberNumber: '',
      },
      ...this.getMdrImportedSourceField(mdrContactInformation),
      context: this.viewData?.context ?? CompanyActionContext.Create,
      isInPopUp: true,
      editedCompanyId: this.viewData?.companyId,
      canBeManagedByCurrentUser: companyCanBeManagedByCurrentUser,
      hasStackedFormFields: insideAddProductMenu,
      hasSendVerificationCodeButtonBelowEmail: this.viewData?.hasMdrSendCodeButtonUnderEmail ?? false,
      required: true,
      isPartner: this.companyType === CompanyType.Partner
    };
  }

  public setCompanyId(companyId: string) {
    // set companyId if missing from view
    if (this.viewData && !this.viewData.companyId) {
      this.viewData.companyId = companyId;
    }
  }

  buildSuccessView() {}

  public addFormChildMdrContactInformation(): void {
    this.formGroup.addFormChild(
      ['specifics', 'mdrContactInformation'],
      this.dom.$mdrContactDetailsSection.getFormGroup()
    );
  }

  public async startMdrContactDetailsSection(mdrContactInformation?: MdrContactInformationDbSchema): Promise<void> {
    const mdrSection = this.dom.$mdrContactDetailsSection;

    toggleVisibility(mdrSection, true);

    // Prevent re-rendering when mdrSection.viewData changes its value. Must render only at mdrSection.start() call!
    mdrSection.propertiesSet = false;
    this.setMdrContactDetailsViewData(mdrContactInformation);

    mdrSection.injectDependencies(this.dependencies);
    await mdrSection.start();
  }

  public updateMdrContactDetailsSection(mdrContactInformation?: MdrContactInformationDbSchema): void {
    // Set data-view-data attribute of the component, and let attributeChangedCallback() do the rendering
    this.setMdrContactDetailsViewData(mdrContactInformation);
    this.dom.$mdrContactDetailsSection.propertiesSet = true;
  }

  private stopMdrContactDetailsSection(): void {
    toggleVisibility(this.dom.$mdrContactDetailsSection, false);
    this.dom.$mdrContactDetailsSection.stop().then();
    this.formGroup.removeFormChild(['specifics', 'mdrContactInformation']);
  }

  private resetLicensePrePaidFormField() {
    this.dom.$licensePrePaidFormField.value = '';
    this.dom.$licensePrePaidFormField.resetField();
    this.dom.$licensePrePaidFormField.Input.readonly = false;
    this.dom.$licensePrePaidFormField.Input.showValidIcon = false;
  }

  private buildIncompatibleAddonWithProductError() {
    this.dom.$incompatibleAddonWithProductError.innerHTML = `
    <span>${htmlEncode(
      this.dependencies.translateFn(
        'The add-on you are trying to add is incompatible with one or more standard products.'
      )
    )}</span><br>
    <gz-link type="underline" color="primary" href="${getLocalizedAsset<string>(
      LicenseConstants.InfoLinks.CompareProducts,
      this.dependencies.getUserLanguage()
    )}">
          <span>${htmlEncode(
            this.dependencies.translateFn('For compatibility details, check the Bitdefender website.')
          )}</span>
    </gz-link>
    `;
  }

  private buildIncompatibleProductNoticeInfoBox() {
    this.dom.$incompatibleProductNotice.innerHTML = `
    <span>${htmlEncode(
      this.dependencies.translateFn(
        'The product you are trying to add will replace a standard product you already have. This may lead to a change of the feature set you will have.'
      )
    )}</span><br>
    <gz-link type="underline" color="primary" href="${getLocalizedAsset<string>(
      LicenseConstants.InfoLinks.CompareProducts,
      this.dependencies.getUserLanguage()
    )}">
          <span>${htmlEncode(this.dependencies.translateFn('Learn more about Bitdefender products'))}</span>
    </gz-link>
    `;
  }

  public buildValidLicenseWithIssuesWarningBox(reason?: string, denyCode?: number) {
    const message =
      reason ||
      this.dependencies.translateFn(
        'An unexpected error has occurred. Please try again. If the issue persists, contact Bitdefender Technical Support.'
      );

    this.dom.$validLicenseWithIssuesWarning.innerHTML = `<span>${message}</span>`;

    if (denyCode === LicenseActivationDenyCodes.REDEEM_NOT_VALID_YET) {
      this.dom.$validLicenseWithIssuesWarning.innerHTML += ` ${this.dependencies.translateFn(
        'For more information on license activation rules, refer to {kbArticleLink}.',
        {
          kbArticleLink: `<gz-link type="underline" color="primary" href="${
            LicenseConstants.InfoLinks.LicenseActivationEffectiveDateFaq
          }"><span>${this.dependencies.translateFn('this KB article')}</span></gz-link>`,
        }
      )}`;
    }
  }

  private resetLicenseDetailsContainer() {
    toggleVisibility(this.dom.licenseDetails.$availableSeatsForServers, false);
    toggleVisibility(this.dom.licenseDetails.$availableSeatsForExchangeMailboxes, false);
    toggleVisibility(this.dom.licenseDetails.$availableSeatsForMobileDevices, false);
    toggleVisibility(this.dom.$incompatibleProductNotice, false);
    toggleVisibility(this.dom.$incompatibleAddonWithProductError, false);
    toggleVisibility(this.dom.licenseDetails.$licenseDetailsContainer, false);
    toggleVisibility(this.dom.$validLicenseWithIssuesWarning, false);

    this.dom.licenseDetails.$productName.value = '';
    this.dom.licenseDetails.$productName.resetField();

    this.dom.licenseDetails.$validity.value = '';
    this.dom.licenseDetails.$validity.resetField();

    this.dom.licenseDetails.$availableSeats.value = '';
    this.dom.licenseDetails.$availableSeats.resetField();

    this.dom.licenseDetails.$availableSeatsForServers.value = '';
    this.dom.licenseDetails.$availableSeatsForServers.resetField();

    this.dom.licenseDetails.$availableSeatsForExchangeMailboxes.value = '';
    this.dom.licenseDetails.$availableSeatsForExchangeMailboxes.resetField();

    this.dom.licenseDetails.$availableSeatsForMobileDevices.value = '';
    this.dom.licenseDetails.$availableSeatsForMobileDevices.resetField();
  }

  private setupLicenseInput() {
    /**
     * Handles the situation when a license key is added. The check button should be enabled if
     * the validators have passed and the input field is non-empty
     */
    this.subscriptions.push(
      this.dom.$licensePrePaidFormField.value$.subscribe({
        next: async licenseKey => {
          this.resetLicenseKeyValidators();
          this.dom.$checkLicenseValidityButton.disabled =
            !(await this.dom.$licensePrePaidFormField.dryValidate()) || licenseKey.length === 0;
        },
      })
    );
  }

  protected onDisconnect() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  buildErrorView(error: Error) {}

  private createFormGroup() {
    this.formGroup = FormGroup.build({
      specifics: {
        license: {
          key: this.dom.$licensePrePaidFormField,
          productId: this.productIdFormField,
        },
      },
    });

    if (
      this.viewData?.context === CompanyActionContext.MyCompany ||
      this.viewData?.context === CompanyActionContext.Edit
    ) {
      this.formGroup.addFormChild(['specifics', 'license', 'isAddon'], this.isAddonProductFormField);
    }
  }

  private setLicenseMessages() {
    this.licenseStatusMessages = {
      [LicenseStatus.PENDING_AUTHENTICATION]: this.dependencies.translateFn('License key is pending authentication'),
      [LicenseStatus.ACTIVE]: this.dependencies.translateFn('License key is active'),
      [LicenseStatus.EXPIRED]: this.dependencies.translateFn('License key is expired'),
      [LicenseStatus.INVALID]: this.dependencies.translateFn('License key is invalid'),
      [LicenseStatus.NOT_EXPIRED]: this.dependencies.translateFn('License key is not expired'),
      [LicenseStatus.CANCELLED]: this.dependencies.translateFn('License key is canceled'),
      [LicenseStatus.NO_LICENSE]: this.dependencies.translateFn('No license key'),
      [LicenseStatus.NOT_AVAILABLE]: this.dependencies.translateFn('License key is not available'),
    };

    this.licenseExceptionMessages = {
      [LicenseExceptions.NO_CONNECTION]: this.dependencies.translateFn(
        'The licensing server is temporarily unavailable. Please try again later.'
      ),
      [LicenseExceptions.NO_INFO]: this.dependencies.translateFn('Could not retrieve license info'),
      [LicenseExceptions.IS_ADDON]: this.dependencies.translateFn('The license key must be for a standard product'),
      [LicenseExceptions.ALREADY_IN_USE]: this.dependencies.translateFn('License key is already in use'),
      [LicenseExceptions.DOES_NOT_HAVE_BASE_LICENSE]: this.dependencies.translateFn(
        'You need to add a product license with EDR first.'
      ),
      [LicenseExceptions.DOES_NOT_HAVE_RM_BASE_LICENSE]: this.dependencies.translateFn(
        'You need to add a product license that provides access to Risk Management first.'
      ),
      [LicenseExceptions.TEMPLATE_DISABLED]: this.dependencies.translateFn(
        'This product is no longer supported. Contact {contact} for further assistance.',
        { contact: this.dependencies.translateFn('Bitdefender Enterprise Support') }
      ),
    };
  }

  private setReturnIncompatibleProducts() {
    this.returnIncompatibleProducts =
      this.viewData?.context === CompanyActionContext.MyCompany || this.viewData?.context === CompanyActionContext.Edit;
  }

  public setCanBeManagedByAbove(canBeManagedByAbove: boolean): void {
    if (this.viewData?.canBeManagedByAbove === canBeManagedByAbove) {
      return;
    }

    if (this.viewData) {
      this.viewData.canBeManagedByAbove = canBeManagedByAbove;
    }

    const viewData = this.dom.$mdrContactDetailsSection.viewData;

    if (this.dom.$mdrContactDetailsSection.started && viewData) {
      viewData.canBeManagedByCurrentUser = canBeManagedByAbove;
      this.dom.$mdrContactDetailsSection.viewData = viewData;
      this.dom.$mdrContactDetailsSection.controller.render().then();
    }
  }

  public setCompanyType(companyType: CompanyType) {
    this.companyType = companyType;
  }

  public getTranslatedLicenseProductId(licenseProductId: LicenseProductIdEnum): string {
    return this.stringsTranslator.translateProductId(licenseProductId);
  }

  private setCustomPrePaidFieldValidators() {
    this.customPrePaidFieldValidators = [
      RequiredValidator(this.commonStrings.requiredField),
      RegexValidator(
        /^(?=(?:.{7}|.{12})$)[A-Za-z0-9]*$/,
        this.dependencies.translateFn(
          'License key must contain {licenseKeyLengthLV2} or {licenseKeyLengthSV4} characters',
          {
            licenseKeyLengthLV2: LicenseConstants.LicenseKeyLengthLV2,
            licenseKeyLengthSV4: LicenseConstants.LicenseKeyLengthSV4,
          }
        )
      ),
      RegexValidator(
        /^[A-Za-z0-9]+$/,
        this.dependencies.translateFn('License key must contain only letters and numbers')
      ),
    ];
  }

  private shouldRemoveValidators() {
    if (
      (this.viewData?.context === CompanyActionContext.MyCompany ||
        this.viewData?.context === CompanyActionContext.Edit) &&
      this.viewData?.action === ActionEnum.addProduct
    ) {
      return false;
    }

    return this.companyType === CompanyType.Partner && this.dom.$licensePrePaidFormField.Input.value.length === 0;
  }

  public updateLicenseKeyValidators(shouldEmptyValidators: boolean, manualValidation: boolean) {
    if (shouldEmptyValidators) {
      this.dom.$licensePrePaidFormField.Validators = [];
    } else {
      this.dom.$licensePrePaidFormField.Validators = this.customPrePaidFieldValidators;
    }

    this.dom.$licensePrePaidFormField.manualValidation = manualValidation;
  }

  public resetLicenseKeyValidators() {
    if (this.shouldRemoveValidators()) {
      this.updateLicenseKeyValidators(true, false);
    } else {
      this.updateLicenseKeyValidators(false, true);
    }
  }

  public setStackedFormFields(isStacked: boolean) {
    const formFields = [...this.dom.$formFields, ...this.dom.$formFieldsToggleable];
    formFields.forEach(field => (field.stacked = isStacked));
  }

  private getMdrImportedSourceField(mdrContactInfo?: MdrContactInformationDbSchema): MdrContactImportSourceValue {
    if (mdrContactInfo?.importedCompanyId) {
      return { importedCompanyId: mdrContactInfo.importedCompanyId };
    }

    if (mdrContactInfo?.importedAccountId) {
      return { importedAccountId: mdrContactInfo.importedAccountId };
    }

    return {};
  }
}
